import { useCallback, useEffect, useMemo, useRef } from "react"
import { debounce } from "lodash"
import type { SerializedDockview } from "dockview"
import type { OPDockviewPersistence } from "connected-components/op-layout-engine/types"
import { generatePanelId, getModuleComponentName } from "connected-components/op-layout-engine/panel-id-utils"
import type { DashboardModule } from "../modules/types"
import { LOADING, OK } from "helpers/data-statuses"
import { useSaveContextPreferences, useReadContextPreference } from "hooks/preferences"
import { warn } from "helpers/dev-logger"
import { ensureGridRoot, pruneLayoutPanels } from "../helpers/prune-layout-panels"

// Dockview grid structure:
// ┌─────────────────────────────────────────────┐
// │                   Root (Branch)             │
// │          Horizontal Split                   │
// ├──────────────────┬──────────────────────────┤
// │                  │                          │
// │  Child 1 (Leaf)  │   Child 2 (Branch)       │
// │  Group "g1"      │   Vertical Split         │
// │  [A, B, C]       │   ├─────────┬────────┐   │
// │                  │   │         │        │   │
// │                  │   │ Leaf    │ Leaf   │   │
// │                  │   │ "g2"    │ "g3"   │   │
// │                  │   │ [D]     │ [E, F] │   │
// └──────────────────┴───┴─────────┴────────┴───┘

/**
 * Derives the expected Dockview panel IDs based on current modules.
 * Uses shared utilities to match OpLayoutEngine's generateStablePanels output.
 */
function deriveExpectedPanelIds(modules: DashboardModule[]): Set<string> {
  const occurrences: Record<string, number> = {}

  return modules.reduce<Set<string>>((panelIds, module) => {
    const componentName = getModuleComponentName(module.type, module.id)
    const count = occurrences[componentName] ?? 0

    panelIds.add(generatePanelId(componentName, count))
    occurrences[componentName] = count + 1

    return panelIds
  }, new Set<string>())
}

/**
 * Reconciles a saved Dockview layout with current dashboard modules.
 *
 * Validates that the saved layout's panels match current modules, pruning invalid panels
 * when possible or returning null to trigger a fresh layout generation. This ensures
 * the layout remains compatible even as modules are added/removed dynamically.
 *
 * @param layout - Saved Dockview layout from preferences
 * @param modules - Current dashboard modules
 * @returns Reconciled layout, or null if layout should be cleared and regenerated
 */
function reconcileLayoutWithModules(
  layout: SerializedDockview,
  modules: DashboardModule[],
): SerializedDockview | null {
  const expectedPanelIds = deriveExpectedPanelIds(modules)

  if (!layout.grid || typeof layout.grid !== "object") {
    warn("Layout has no grid structure, clearing")
    return null
  }

  let reconciledLayout = layout

  if (!layout.grid.root) {
    if (!layout.floatingGroups?.length) {
      warn("Layout has no grid root and no floating groups, clearing")
      return null
    }

    reconciledLayout = {
      ...layout,
      grid: ensureGridRoot(layout.grid),
    }
  }

  if (!reconciledLayout.panels || Array.isArray(reconciledLayout.panels) || typeof reconciledLayout.panels !== "object") {
    warn("Layout has no panels object, clearing")
    return null
  }

  const initialSavedPanelIds = Object.keys(reconciledLayout.panels)

  if (initialSavedPanelIds.length === 0) {
    warn("Layout has empty panels, clearing")
    return null
  }

  const hasUnknownPanels = initialSavedPanelIds.some((id) => !expectedPanelIds.has(id))

  if (hasUnknownPanels) {
    const prunedLayout = pruneLayoutPanels(reconciledLayout, expectedPanelIds)

    if (!prunedLayout) {
      warn("Layout contains panels that no longer exist, clearing layout")
      return null
    }

    warn("Layout contains panels that no longer exist, pruning layout")
    reconciledLayout = prunedLayout
  }

  const finalSavedPanelIds = Object.keys(reconciledLayout.panels)
  const savedSet = new Set(finalSavedPanelIds)
  const expectedArray = Array.from(expectedPanelIds)

  // If saved layout is subset of expected, keep it and let incremental adds fill the rest
  if (savedSet.size !== expectedPanelIds.size) {
    return reconciledLayout
  }

  const allMatch = expectedArray.every((id) => savedSet.has(id))
  if (!allMatch) {
    warn("Panel IDs don't match current modules, clearing layout")
    return null
  }

  return reconciledLayout
}

/**
 * Hook for managing OpLayoutEngine persistence in the Dashboard.
 *
 * Coordinates loading/saving Dockview layouts with user preferences, reconciling
 * saved layouts with current modules to handle dynamic module changes gracefully.
 * Provides load/save/clear operations and loading state for the OpLayoutEngine.
 *
 * @param modules - Current dashboard modules
 * @param isModulesLoaded - Whether modules have finished loading
 * @returns Persistence API with load/save/clear methods and loading state
 */
export function useDashboardOpLayoutEnginePersistence(modules: DashboardModule[], isModulesLoaded: boolean): OPDockviewPersistence {
  const savePreferences = useSaveContextPreferences("dockviewLayout") as unknown as (value: SerializedDockview | null) => void
  const [storedLayout, status] = useReadContextPreference("dockviewLayout", null) as [SerializedDockview | null | undefined, string]

  const lastSavedRef = useRef<string | null>(null)

  // Debounce by 500ms to batch rapid layout changes
  const debouncedSave = useMemo(
    () => debounce(savePreferences, 500),
    [savePreferences],
  )

  useEffect(() => {
    return () => {
      // Flush any pending saves to ensure layout changes are persisted before unmount
      debouncedSave.flush()
      lastSavedRef.current = null
    }
  }, [debouncedSave])

  const reconciledLayout = useMemo(() => {
    if (!isModulesLoaded || status !== OK) {
      return null
    }

    if (!storedLayout || typeof storedLayout !== "object") {
      return null
    }

    return reconcileLayoutWithModules(storedLayout, modules)
  }, [status, storedLayout, isModulesLoaded, modules])

  const persistState = useCallback((state: SerializedDockview | null) => {
    const newSerialized = state ? JSON.stringify(state) : null

    if (newSerialized === lastSavedRef.current) {
      return
    }

    lastSavedRef.current = newSerialized

    debouncedSave(state)
  }, [debouncedSave])

  return useMemo<OPDockviewPersistence>(() => ({
    load: () => {
      if (!isModulesLoaded || status !== OK) {
        return undefined
      }
      // null means "no saved layout exists, generate default from components".
      lastSavedRef.current = reconciledLayout ? JSON.stringify(reconciledLayout) : null
      return reconciledLayout
    },
    save: (state: SerializedDockview) => {
      persistState(state)
    },
    clear: () => {
      debouncedSave.cancel()
      lastSavedRef.current = null
      savePreferences(null)
    },
    loading: status === LOADING || !isModulesLoaded || modules.length === 0,
  }), [persistState, savePreferences, debouncedSave, status, isModulesLoaded, modules.length, reconciledLayout])
}
